import { api } from './client'
import { downloadFile } from '@/lib/download'
import type { OrgSettings, Branding, ModuleFlag, BackupRow } from '@/types/admin'

// ─── Org ───────────────────────────────────────────────────────────────
// Backend uses `financial_year_start`, `epf_code`, `pt_registration`.
// Frontend uses `fiscal_year_start_month`, `pf_establishment_code`, `pt_state_code`.

type OrgApi = Omit<OrgSettings, 'fiscal_year_start_month' | 'pf_establishment_code' | 'pt_state_code'> & {
  financial_year_start?: number
  epf_code?: string | null
  pt_registration?: string | null
}

function fromOrgApi(d: OrgApi): OrgSettings {
  const { financial_year_start, epf_code, pt_registration, ...rest } = d
  return {
    ...rest,
    fiscal_year_start_month: financial_year_start,
    pf_establishment_code: epf_code,
    pt_state_code: pt_registration,
  }
}

function toOrgApi(input: Partial<OrgSettings>): Partial<OrgApi> {
  const { fiscal_year_start_month, pf_establishment_code, pt_state_code, ...rest } = input
  const out: Partial<OrgApi> = { ...rest }
  if (fiscal_year_start_month !== undefined) out.financial_year_start = fiscal_year_start_month
  if (pf_establishment_code !== undefined) out.epf_code = pf_establishment_code
  if (pt_state_code !== undefined) out.pt_registration = pt_state_code
  return out
}

export async function getOrg(): Promise<OrgSettings> {
  const res = await api.get('/api/v1/settings/org')
  return fromOrgApi(res.data as OrgApi)
}
export async function updateOrg(input: Partial<OrgSettings>): Promise<OrgSettings> {
  const res = await api.patch('/api/v1/settings/org', toOrgApi(input))
  return fromOrgApi(res.data as OrgApi)
}

// ─── Branding ──────────────────────────────────────────────────────────
// Backend uses `font_family`; frontend uses `font`.

type BrandingApi = Omit<Branding, 'font'> & { font_family?: string | null }

function fromBrandingApi(d: BrandingApi): Branding {
  const { font_family, ...rest } = d
  return { ...rest, font: font_family }
}
function toBrandingApi(input: Partial<Branding>): Partial<BrandingApi> {
  const { font, ...rest } = input
  const out: Partial<BrandingApi> = { ...rest }
  if (font !== undefined) out.font_family = font
  return out
}

export async function getBranding(): Promise<Branding> {
  const res = await api.get('/api/v1/settings/branding')
  return fromBrandingApi(res.data as BrandingApi)
}
export async function updateBranding(input: Partial<Branding>): Promise<Branding> {
  // logo_url is set only by the logo upload endpoint; never echo it back from the form.
  const { logo_url: _logo, logo_file_id: _file, signature_url: _sig, seal_url: _seal, ...rest } = input
  void _logo; void _file; void _sig; void _seal
  const res = await api.patch('/api/v1/settings/branding', toBrandingApi(rest))
  return fromBrandingApi(res.data as BrandingApi)
}
/** PNG / JPEG / WebP, 2 MB max — validated again (by content) on the server. */
export async function uploadLogo(file: File): Promise<Branding> {
  const fd = new FormData()
  fd.append('logo', file)
  const res = await api.post('/api/v1/settings/branding/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  return fromBrandingApi(res.data as BrandingApi)
}

/** Letters' signature or company seal: PNG / JPEG, 1 MB max (content-checked on the server). `null` removes it. */
export async function uploadSignatureImage(kind: 'signature' | 'seal', file: File | null): Promise<Branding> {
  const fd = new FormData()
  fd.append('kind', kind)
  if (file) fd.append('image', file)
  else fd.append('remove', '1')
  const res = await api.post('/api/v1/settings/branding/signature-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  return fromBrandingApi(res.data as BrandingApi)
}

// ─── Modules ───────────────────────────────────────────────────────────
// Backend returns `module_key`, `module_name`, `is_enabled`, `is_locked`.
// Frontend uses `module`, `name`, `enabled`, `locked`. PATCH body must use `is_enabled`.

type ModuleFlagApi = {
  id: string
  module_key: string
  module_name?: string | null
  is_enabled: boolean
  is_locked?: boolean
  is_core?: boolean
  description?: string | null
}

function fromModuleApi(d: ModuleFlagApi): ModuleFlag {
  return {
    module: d.module_key,
    name: d.module_name,
    enabled: !!d.is_enabled,
    locked: !!d.is_locked,
    core: !!d.is_core,
    description: d.description,
  }
}

export async function listModules(): Promise<ModuleFlag[]> {
  const res = await api.get('/api/v1/settings/modules')
  const d = res.data as ModuleFlagApi[] | { items: ModuleFlagApi[] }
  const items = Array.isArray(d) ? d : d.items
  return items.map(fromModuleApi)
}
export async function toggleModule(module: string, enabled: boolean): Promise<ModuleFlag> {
  const res = await api.patch(`/api/v1/settings/modules/${module}`, { is_enabled: enabled })
  return fromModuleApi(res.data as ModuleFlagApi)
}

// ─── Backups ───────────────────────────────────────────────────────────
export async function listBackups(): Promise<BackupRow[]> {
  const res = await api.get('/api/v1/settings/backup')
  const d = res.data as BackupRow[] | { items: BackupRow[] }
  return Array.isArray(d) ? d : d.items
}
export async function runBackupNow(): Promise<BackupRow[]> {
  // 201 carries the new backup + the list after retention; a failed backup is a 500 with the reason.
  const res = await api.post('/api/v1/settings/backup/now')
  const d = res.data as { items?: BackupRow[] } | BackupRow[]
  if (Array.isArray(d)) return d
  return d.items ?? []
}
export async function deleteBackup(id: string): Promise<void> {
  await api.delete(`/api/v1/settings/backup/${encodeURIComponent(id)}`)
}

/** Restore the whole database from a backup. The server requires the typed confirmation. */
export async function restoreBackup(id: string): Promise<{ restored: string; safety_backup: string; message?: string }> {
  const res = await api.post(`/api/v1/settings/backup/${encodeURIComponent(id)}/restore`, { confirm: 'RESTORE' })
  return res.data as { restored: string; safety_backup: string; message?: string }
}
export async function downloadBackup(id: string): Promise<void> {
  await downloadFile(`/api/v1/settings/backup/${encodeURIComponent(id)}/download`, { filename: id })
}

/** Which modules are switched off for the signed-in user's organisation. */
export async function getMyModules(): Promise<{ modules: Record<string, boolean>; disabled: string[] }> {
  const res = await api.get('/api/v1/me/modules')
  return res.data as { modules: Record<string, boolean>; disabled: string[] }
}

/** Restore preview: the backup restored into a scratch database, counted per table against live. */
export interface BackupPreview {
  backup: string
  created_at: string
  scratch: string
  tables: { table: string; backup_rows: number | null; live_rows: number | null; change: number }[]
  totals: { backup_rows: number; live_rows: number; tables: number }
  can_commit: boolean
}
export async function previewBackupRestore(id: string): Promise<BackupPreview> {
  return (await api.get(`/api/v1/settings/backup/${encodeURIComponent(id)}/preview`)).data as BackupPreview
}
export interface BackupDrillResult { ok: boolean; backup: string; tables: number; backup_rows: number; core: Record<string, number>; message: string }
export async function runBackupDrill(id?: string): Promise<BackupDrillResult> {
  return (await api.post('/api/v1/settings/backup/drill', id ? { id } : {})).data as BackupDrillResult
}

/** Scheduler heartbeat from the public health endpoint (stale = cron has stopped). */
export interface SchedulerHealth { last_beat_at: string | null; age_seconds: number | null; stale: boolean; stale_after_seconds: number }
export async function schedulerHealth(): Promise<SchedulerHealth | null> {
  const d = (await api.get('/api/v1/health')).data as { scheduler?: SchedulerHealth }
  return d?.scheduler ?? null
}
